import React, { useState, useEffect } from 'react';
import { supabase } from '@/integrations/supabase/client';

// Simple in-memory cache for the SPA lifetime (no reload)
const thumbCache = new Map<string, string[]>();

interface CollectionFanPreviewProps {
  eventId: string;
  raceId: string;
  participantName: string;
  participantId: string; // The participant UUID
}

export const CollectionFanPreview: React.FC<CollectionFanPreviewProps> = ({
  eventId,
  raceId,
  participantName,
  participantId
}) => {
  const [thumbnails, setThumbnails] = useState<string[]>([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    let isMounted = true;
    const key = `${eventId}:${raceId}:${participantId}`;

    const fetchThumbnails = async () => {
      // Serve from cache if available
      const cached = thumbCache.get(key);
      if (cached && isMounted) {
        setThumbnails(cached);
        setLoading(false);
        return;
      }

      try {
        // Call compute-participant-ranges for this specific participant
        const { data, error } = await supabase.functions.invoke('compute-participant-ranges', {
          body: { 
            eventId, 
            raceId, 
            participantId // Pass participant UUID
          }
        });

        if (!error && data?.participants && Array.isArray(data.participants) && data.participants.length > 0) {
          const participant = data.participants[0];
          const urls: string[] = participant?.photo_range?.urls || [];
          const pickThree = (arr: string[]) => {
            const n = arr.length;
            if (n === 0) return [];
            if (n <= 3) return arr;
            const mid = Math.floor(n / 2);
            return [arr[0], arr[mid], arr[n - 1]];
          };
          const sampled = pickThree(urls);
          if (isMounted) {
            setThumbnails(sampled);
            thumbCache.set(key, sampled);
          }
          return;
        } else {
        }
      } catch (err) {
        console.error('Error loading thumbnails:', err);
      } finally {
        if (isMounted) setLoading(false);
      }
    };

    fetchThumbnails();

    return () => {
      isMounted = false;
    };
  }, [eventId, raceId, participantId]);

  if (loading) {
    return (
      <div className="w-20 h-16 flex items-center justify-center">
        <div className="animate-pulse bg-muted rounded w-full h-full" />
      </div>
    );
  }

  // No explicit empty state; we'll show placeholder cards if thumbnails are missing

  return (
    <div className="relative w-20 h-16 flex items-end justify-center">
      {[0, 1, 2].map((i) => {
        const thumbnail = thumbnails[i] || null;
        return (
          <div
            key={i}
            className="absolute w-12 h-14 rounded-md overflow-hidden shadow-lg border-2 border-white bg-card transition-transform"
            style={{
              transform: `rotate(${(i - 1) * 15}deg) translateY(${i === 1 ? '0px' : '4px'})`,
              zIndex: i === 1 ? 3 : i === 0 ? 2 : 1,
              left: `${i * 6}px`,
            }}
          >
            {thumbnail ? (
              <img
                src={thumbnail}
                alt={`${participantName} - Preview ${i + 1}`}
                className="w-full h-full object-cover"
                loading="lazy"
                onError={(e) => {
                  console.error('Failed to load thumbnail:', thumbnail);
                  e.currentTarget.style.display = 'none';
                }}
              />
            ) : (
              <div className="w-full h-full bg-muted" />
            )}
          </div>
        );
      })}
    </div>
  );
};
